import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { X, User, Mail, Phone, FileText } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";

const RunRegistrationForm = ({ meeting, onClose, onSuccess }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { execute: executeSubmit, loading: submitLoading } = useApi();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  // Remove old loading state - now using useApi hook
  const [errors, setErrors] = useState({});

  // Auto-fill form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      }));
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const userId = user?.id;
      
      const registrationData = {
        ...formData,
        user_id: userId,
        meeting_id: meeting.id, // Use the meeting ID as the meeting ID
        auto_register_future: false, // Always join this meeting only (no option to join whole sequence)
      };
      
      const response = await executeSubmit(
        () => apiService.clubs.joinRun(meeting.club_id || meeting.id, registrationData),
        { 
          showSuccessToast: true,
          successMessage: 'Registration successful!',
          showErrorToast: false // We'll handle errors manually
        }
      );
      
      onSuccess && onSuccess(response);
      onClose();
    } catch (error) {
      console.error('Registration error:', error);

      const detail = error.response?.data?.detail;
      let errorMessage = 'Unable to register for this run. Please try again.';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((d) => (d && typeof d === 'object' && 'msg' in d ? d.msg : String(d))).join(' ') || errorMessage;
      } else if (detail && typeof detail === 'object' && 'msg' in detail) {
        errorMessage = detail.msg;
      } else if (detail != null) {
        errorMessage = String(detail);
      } else if (error.message) {
        errorMessage = error.message;
      }

      const status = error.response?.status;

      if (status === 409) {
        toast.error('Already Registered', {
          description: 'You are already registered for this run. You cannot register twice for the same run.',
          duration: 5000,
        });
      } else if (status === 403 && typeof errorMessage === 'string' && errorMessage.includes('Complete all required fields on your RunCard')) {
        toast.error('Complete your RunCard to join', {
          description: errorMessage,
          duration: 7000,
          action: {
            label: 'Complete RunCard',
            onClick: () => {
              onClose?.();
              navigate('/profile');
            },
          },
        });
      } else {
        toast.error('Registration Failed', {
          description: errorMessage,
          duration: 5000,
        });
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg bg-white/95 backdrop-blur-xl border-white/30 shadow-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-gray-900 text-xl font-bold" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Join {meeting.clubName || meeting.name}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-600 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Field */}
            <div>
              <Label htmlFor="name" className="text-gray-800 text-sm font-medium">Full Name <span className="text-red-500">*</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`pl-10 bg-gray-50 text-gray-900 placeholder:text-gray-500 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    errors.name ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''
                  }`}
                  required
                />
              </div>
              {errors.name && (
                <p className="text-red-600 text-xs mt-1 font-medium">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <Label htmlFor="email" className="text-gray-800 text-sm font-medium">Email <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`pl-10 bg-gray-50 text-gray-900 placeholder:text-gray-500 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''
                  }`}
                  required
                />
              </div>
              {errors.email && (
                <p className="text-red-600 text-xs mt-1 font-medium">{errors.email}</p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <Label htmlFor="phone" className="text-gray-800 text-sm font-medium">Phone Number <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`pl-10 bg-gray-50 text-gray-900 placeholder:text-gray-500 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    errors.phone ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''
                  }`}
                  required
                />
              </div>
              {errors.phone && (
                <p className="text-red-600 text-xs mt-1 font-medium">{errors.phone}</p>
              )}
            </div>

            {/* Notes Field */}
            <div>
              <Label htmlFor="notes" className="text-gray-800 text-sm font-medium">Additional Notes (Optional)</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 text-gray-500 w-4 h-4" />
                <Textarea
                  id="notes"
                  placeholder="Any additional information or special requests..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="pl-10 bg-gray-50 text-gray-900 placeholder:text-gray-500 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
                  rows={2}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                disabled={submitLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-black text-white hover:bg-gray-800"
                disabled={submitLoading}
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                {submitLoading ? 'Joining...' : 'Join Run'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RunRegistrationForm;
